import axios from 'axios';
import { useEffect, useState, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';

interface GameConfigData {
    isAutoPlayRunning: boolean;
    isAutoForceWinRunning: boolean;
    autoForceWinRoomPercent: number;
    isAutoForceWinRunningScheduled: boolean;
    autoForceWinRunningStartTime: number;
    autoForceWinRunningEndTime: number;
}

export default function GameSystemConfiguration() {
    const [editState, setEditState] = useState<boolean>(false);
    const [configData, setConfigData] = useState<GameConfigData>({
        isAutoPlayRunning: false,
        isAutoForceWinRunning: false,
        autoForceWinRoomPercent: 0,
        isAutoForceWinRunningScheduled: false,
        autoForceWinRunningStartTime: 0,
        autoForceWinRunningEndTime: 0,
    });

    const getGameConfiguration = useCallback(() => {
        const token = localStorage.getItem('token');
        axios
            .get(`${process.env.REACT_APP_ADMIN_API}/system/game-config`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                if (res.data) {
                    const data = res.data.filter((e: { game_config_type: string }) => e.game_config_type === 'GSQUID_SETTINGS');
                    const {
                        isAutoPlayRunning,
                        isAutoForceWinRunning,
                        autoForceWinRoomPercent,
                        isAutoForceWinRunningScheduled,
                        autoForceWinRunningStartTime,
                        autoForceWinRunningEndTime,
                    } = data[0].game_config_data;

                    const newConfigData = {
                        isAutoPlayRunning,
                        isAutoForceWinRunning,
                        autoForceWinRoomPercent,
                        isAutoForceWinRunningScheduled: isAutoForceWinRunningScheduled || false,
                        autoForceWinRunningStartTime: autoForceWinRunningStartTime || 0,
                        autoForceWinRunningEndTime: autoForceWinRunningEndTime || 0,
                    };

                    // Only update state if data has changed
                    setConfigData((prevData) => {
                        const hasChanged = JSON.stringify(prevData) !== JSON.stringify(newConfigData);
                        return hasChanged ? newConfigData : prevData;
                    });
                }
            })
            .catch((err) => {
                console.log(err);
            });
    }, []);

    useEffect(() => {
        getGameConfiguration();
    }, [getGameConfiguration]);

    function updateConfiguration() {
        if (editState && configData) {
            const token = localStorage.getItem('token');

            axios
                .post(
                    `${process.env.REACT_APP_ADMIN_API}/system/game-config/gsquid-settings`,
                    {
                        ...configData,
                        type: 'GSQUID_SETTINGS',
                    },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                )
                .then((_) => {
                    Swal.fire({
                        title: 'Success!',
                        text: 'Game configuration updated successfully.',
                        icon: 'success',
                        timer: 2000,
                        timerProgressBar: true,
                    });
                    setEditState(!editState);
                    getGameConfiguration();
                })
                .catch((err) => {
                    console.log(err);
                    Swal.fire({
                        title: 'Error!',
                        text: 'Failed to update game configuration.',
                        icon: 'error',
                        timer: 2000,
                        timerProgressBar: true,
                    });
                });
        } else {
            setEditState(!editState);
        }
    }

    function cancelEdit() {
        setEditState(false);
        getGameConfiguration(); // Reset to original configuration
    }

    const handleInputChange = (field: keyof GameConfigData, value: boolean | number) => {
        setConfigData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // Memoize time formatting to prevent unnecessary recalculations
    const formattedTimes = useMemo(
        () => ({
            startTime:
                String(Math.floor(configData.autoForceWinRunningStartTime / 100)).padStart(2, '0') +
                ':' +
                String(configData.autoForceWinRunningStartTime % 100).padStart(2, '0'),
            endTime:
                String(Math.floor(configData.autoForceWinRunningEndTime / 100)).padStart(2, '0') +
                ':' +
                String(configData.autoForceWinRunningEndTime % 100).padStart(2, '0'),
        }),
        [configData.autoForceWinRunningStartTime, configData.autoForceWinRunningEndTime]
    );

    return (
        <div className="w-full h-full">
            <div className="h-full flex">
                <div className="flex flex-col justify-center w-full h-max p-6 pt-20 md:flex-row md:mt-10 flex-wrap">
                    <div className="w-full max-w-4xl mx-auto">
                        <div className="flex flex-col md:flex-row justify-between gap-3 items-center mb-6">
                            <h1 className="text-2xl font-bold text-gray-800">Game Configuration</h1>
                            <div className="flex gap-2">
                                {editState && (
                                    <button
                                        onClick={cancelEdit}
                                        className="px-6 py-2 rounded-lg font-medium transition-colors bg-gray-500 hover:bg-gray-600 text-white"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    onClick={updateConfiguration}
                                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                                        editState
                                            ? 'bg-green-500 hover:bg-green-600 text-white'
                                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                                    }`}
                                >
                                    {editState ? 'Save Configuration' : 'Edit Configuration'}
                                </button>
                            </div>
                        </div>

                        {editState && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Auto Play Setting */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h2 className="text-lg font-semibold mb-4 text-gray-700">Auto Play Setting</h2>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium text-gray-600">Running</label>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={configData.isAutoPlayRunning}
                                                    onChange={(e) => handleInputChange('isAutoPlayRunning', e.target.checked)}
                                                    disabled={!editState}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium text-gray-600">Schedule Mode</label>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={configData.isAutoForceWinRunningScheduled}
                                                    onChange={(e) => handleInputChange('isAutoForceWinRunningScheduled', e.target.checked)}
                                                    disabled={!editState}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        {configData.isAutoForceWinRunningScheduled && (
                                            <>
                                                <div className="flex items-center justify-between">
                                                    <label className="text-sm font-medium text-gray-600">Start Time</label>
                                                    <input
                                                        type="time"
                                                        value={formattedTimes.startTime}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(':');
                                                            const timeValue = parseInt(hours) * 100 + parseInt(minutes);
                                                            handleInputChange('autoForceWinRunningStartTime', timeValue);
                                                        }}
                                                        disabled={!editState}
                                                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                                    />
                                                </div>

                                                <div className="flex items-center justify-between">
                                                    <label className="text-sm font-medium text-gray-600">End Time</label>
                                                    <input
                                                        type="time"
                                                        value={formattedTimes.endTime}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(':');
                                                            const timeValue = parseInt(hours) * 100 + parseInt(minutes);
                                                            handleInputChange('autoForceWinRunningEndTime', timeValue);
                                                        }}
                                                        disabled={!editState}
                                                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Auto Force Win Settings */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h2 className="text-lg font-semibold mb-4 text-gray-700">Auto Force Win Settings</h2>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium text-gray-600">Running</label>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={configData.isAutoForceWinRunning}
                                                    onChange={(e) => handleInputChange('isAutoForceWinRunning', e.target.checked)}
                                                    disabled={!editState}
                                                />
                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                            </label>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium text-gray-600">Target Win Rate</label>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    value={configData.autoForceWinRoomPercent}
                                                    onChange={(e) => handleInputChange('autoForceWinRoomPercent', parseInt(e.target.value))}
                                                    disabled={!editState}
                                                    className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                                                />
                                                <span className="text-sm text-gray-500">%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Configuration Summary */}
                        <div className="mt-8 bg-blue-50 p-4 rounded-lg">
                            <h2 className="text-lg font-semibold mb-4 text-blue-800">Current Configuration</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Auto Play Section */}
                                <div className="bg-white p-4 rounded-lg border border-blue-200">
                                    <h3 className="text-base font-semibold mb-3 text-blue-700">Auto Play</h3>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Running Status:</span>
                                            <span
                                                className={`px-2 py-1 rounded text-xs font-medium ${
                                                    configData.isAutoPlayRunning ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}
                                            >
                                                {configData.isAutoPlayRunning ? 'Running' : 'Stopped'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Schedule Mode:</span>
                                            <span
                                                className={`px-2 py-1 rounded text-xs font-medium ${
                                                    configData.isAutoForceWinRunningScheduled
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-green-100 text-green-800'
                                                }`}
                                            >
                                                {configData.isAutoForceWinRunningScheduled ? 'Scheduled' : 'All Time'}
                                            </span>
                                        </div>

                                        {configData.isAutoForceWinRunningScheduled && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-600">Time Range:</span>
                                                <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                    {formattedTimes.startTime}
                                                    <span className="mx-1">-</span>
                                                    {formattedTimes.endTime}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Auto Force Win Section */}
                                <div className="bg-white p-4 rounded-lg border border-blue-200">
                                    <h3 className="text-base font-semibold mb-3 text-blue-700">Auto Force Win</h3>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Running Status:</span>
                                            <span
                                                className={`px-2 py-1 rounded text-xs font-medium ${
                                                    configData.isAutoForceWinRunning
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                }`}
                                            >
                                                {configData.isAutoForceWinRunning ? 'Running' : 'Stopped'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-gray-600">Target Win Rate:</span>
                                            <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                {configData.autoForceWinRoomPercent}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
